/**
 * TopStories.jsx — Dashboard card showing trending stories
 * Fetches the top 4 most-liked stories from Firestore
 * and displays them in a compact, clickable list.
 */

import { useState, useEffect } from 'react';
import { useNavigate }         from 'react-router-dom';
import { db }                  from '../firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { STORY_THEMES }        from './StoryWriter';

const MOOD_EMOJIS = {
  'real-journey':   '🌱',
  'motivational':   '🔥',
  'fiction':        '📖',
  'horror':         '👻',
  'romance':        '💕',
  'mental-health':  '🧠',
  'comedy':         '😄',
  'self-discovery': '✨',
};

export default function TopStories() {
  const navigate = useNavigate();
  const [stories, setStories]   = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    async function fetchTopStories() {
      try {
        const q = query(
          collection(db, 'stories'),
          orderBy('likes', 'desc'),
          limit(4)
        );
        const snap = await getDocs(q);
        setStories(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('TopStories fetch error:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchTopStories();
  }, []);

  const getTheme = (themeId) =>
    STORY_THEMES.find(t => t.id === themeId) || { color: '#6B6258', light: '#F7F3EE', label: 'Story' };

  // ─── Skeleton while loading ───────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: '4px',
        }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>📖 Top Stories</h3>
        </div>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{
            height: '62px', borderRadius: '12px',
            background: 'var(--border-color, #eee)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }} />
        ))}
      </div>
    );
  }

  // ─── Empty state ──────────────────────────────────────────────────────
  if (!stories.length) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>📖 Top Stories</h3>
        <div style={{
          textAlign: 'center', padding: '32px 16px',
          color: 'var(--text-secondary-color, #888)',
          fontSize: '0.88rem',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📝</div>
          No stories yet. Be the first to share yours!
          <div style={{ marginTop: '12px' }}>
            <button
              onClick={() => navigate('/Resources')}
              style={{
                padding: '8px 18px', background: 'var(--accent-color, #c95b3d)',
                color: '#fff', border: 'none', borderRadius: '10px',
                fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
              }}
            >
              Go to Stories →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Story list ───────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: '2px',
      }}>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>📖 Top Stories</h3>
        <button
          onClick={() => navigate('/Resources')}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '0.78rem', fontWeight: 600,
            color: 'var(--accent-color, #c95b3d)',
            padding: 0,
          }}
        >
          View all →
        </button>
      </div>

      {stories.map((story, idx) => {
        const theme    = getTheme(story.theme);
        const emoji    = MOOD_EMOJIS[story.theme] || '📖';
        const plain    = (story.content || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        const preview  = plain.split(' ').slice(0, 14).join(' ');
        const likes    = story.likes || 0;
        const words    = story.wordCount || 0;
        const readTime = Math.max(1, Math.ceil(words / 200));

        return (
          <div
            key={story.id}
            onClick={() => navigate('/Resources')}
            style={{
              display: 'flex', gap: '12px', alignItems: 'flex-start',
              padding: '12px 14px', borderRadius: '14px',
              background: idx === 0 ? theme.light : 'var(--card-bg-secondary, #f8f9fa)',
              border: idx === 0
                ? `1.5px solid ${theme.color}30`
                : '1px solid var(--border-color, #e8e8e8)',
              cursor: 'pointer',
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {/* Rank + emoji badge */}
            <div style={{
              width: '38px', height: '38px', borderRadius: '10px',
              background: idx === 0 ? theme.color : 'var(--border-color, #e0e0e0)',
              color: idx === 0 ? '#fff' : 'var(--text-color, #333)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: idx === 0 ? '1.1rem' : '0.85rem',
              fontWeight: 800, flexShrink: 0,
              fontFamily: "'Outfit', system-ui, sans-serif",
            }}>
              {idx === 0 ? emoji : `#${idx + 1}`}
            </div>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '0.88rem', fontWeight: 700,
                color: 'var(--text-color, #161412)',
                lineHeight: 1.3, marginBottom: '4px',
                overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {story.title}
              </div>

              <div style={{
                fontSize: '0.78rem', color: 'var(--text-secondary-color, #6B6258)',
                lineHeight: 1.4, marginBottom: '6px',
                overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {preview}{plain.split(' ').length > 14 ? '…' : ''}
              </div>

              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                fontSize: '0.72rem', color: 'var(--text-muted, #A09890)',
              }}>
                <span style={{
                  padding: '2px 8px', borderRadius: '6px',
                  background: `${theme.color}15`, color: theme.color,
                  fontWeight: 600, fontSize: '0.68rem',
                }}>
                  {theme.label}
                </span>
                <span>❤️ {likes}</span>
                <span>{readTime} min</span>
                <span style={{
                  marginLeft: 'auto', fontWeight: 600,
                  color: 'var(--text-color, #333)',
                }}>
                  {story.authorName || 'Anonymous'}
                </span>
              </div>
            </div>
          </div>
        );
      })}

      {/* CTA at bottom */}
      <button
        onClick={() => navigate('/Resources')}
        style={{
          width: '100%', padding: '10px',
          background: 'var(--card-bg-secondary, #f8f9fa)',
          border: '1px dashed var(--border-color, #ddd)',
          borderRadius: '12px', cursor: 'pointer',
          fontSize: '0.82rem', fontWeight: 600,
          color: 'var(--text-secondary-color, #666)',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--border-color, #eee)'}
        onMouseLeave={e => e.currentTarget.style.background = 'var(--card-bg-secondary, #f8f9fa)'}
      >
        ✍️ Read or write a story
      </button>
    </div>
  );
}